import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Download, Eye, X, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Employee360PensionApplicationForm = ({ employeeId, onClose }) => {
  const { auth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const printContainerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`/v1/employee-master/${employeeId}/pension-application-form`);
        setData(response.data);
      } catch (err) {
        console.error('Error fetching pension application form data', err);
        setError('Failed to load pension application form data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [employeeId]);

  const loadHtml2Pdf = async () => {
    if (!window.html2pdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
  };

  const handleDownloadPDF = async () => {
    if (!data || !printContainerRef.current) return;

    setGeneratingPdf(true);

    try {
      await loadHtml2Pdf();

      const element = printContainerRef.current;

      const opt = {
        margin: [0, 0, 0, 0],
        filename: `Pension_Application_Form_${data.employee.emp_code || employeeId}.pdf`,
        image: {
          type: 'jpeg',
          quality: 0.98
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          scrollX: 0,
          scrollY: 0
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        },
        pagebreak: {
          mode: ['css', 'legacy']
        }
      };

      await window
        .html2pdf()
        .set(opt)
        .from(element)
        .save();
    } catch (err) {
      console.error('Failed to generate PDF', err);
    } finally {
      setGeneratingPdf(false);
    }
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mr-2" />
        <span className="text-gray-600 font-medium">Loading form data...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center text-red-600 font-medium">
        {error || 'Data could not be loaded.'}
      </div>
    );
  }

  const { employee, pension, gratuity, family_pension, commutation, pensioner_profile, nominees, qualifying_service, organization, application_date } = data;

  const formatDateDash = (dateStr) => {
    if (!dateStr) return '';
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return '';
    const d = String(dateObj.getDate()).padStart(2, '0');
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const y = dateObj.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const formatDateSlash = (dateStr) => formatDateDash(dateStr);

  // Find spouse if exists
  const spouseNominee = nominees.find(n => ['wife', 'husband', 'spouse'].includes(n.relationship.toLowerCase()));
  const spouseName = spouseNominee ? `${spouseNominee.nominee_name}` : '';

  // Render individual page contents
  const renderPage = (pageNum) => {
    switch (pageNum) {
      case 1:
        return (
          <div className="pension-form-page mt-1 p-1">
            <div className="text-center text-xs font-bold font-serif mb-2 mt-5">FORM 5</div>
            <div className="text-center text-xs font-serif leading-tight mb-4">
              (See rules 58(1), 63(1), 66 and 82 (1) of CC S (P) Rule 1972)<br /><br />
              <span className="text-sm font-bold tracking-wider">APPLICATION FOR PENSION</span>
            </div>
            <div className="grid grid-cols-2 text-xs font-serif mb-6">
              <div></div>
              <div className="pl-8 text-right">
                <strong>From:</strong> {employee.honorific} {employee.full_name}<br />
                {employee.current_address}
              </div>
            </div>
            <div className="text-xs font-serif mb-4">
              <strong>To,</strong><br />
              Chief Executive Officer<br />
              Pune Cantonment Board,<br />
              Pune- 1
            </div>
            <div className="text-center text-xs font-bold font-serif mb-6 border-b border-black pb-1">
              SUB: APPLICATION FOR SANCTION OF PENSION.
            </div>
            <div className="text-xs font-serif space-y-4 leading-relaxed text-justify">
              <p>Sir,</p>
              <p>
                I beg to say that I am due to retire from service with effect from {formatDateDash(employee.retirement_date)}. My date of birth is {formatDateDash(employee.date_of_birth)}.
                I therefore request that steps may kindly be taken with a view to the pension and gratuity admissible to me being sanctioned by the date of my retirement.
                I desire to draw my pension from {pension.pension_commencement_date ? formatDateDash(pension.pension_commencement_date) : formatDateDash(employee.retirement_date)}.
              </p>
              <p>
                2. I hereby declare that I have neither applied for nor received any pension or gratuity in respect of any portion of the service qualifying for this pension and in respect of which pension and in respect of which pension and gratuity are claimed here in nor shall submit an application here after without quoting a reference to this application and the orders which may be passed here on.
              </p>
              <p>
                2A. I am governed by Rule 54 of the CCS (Pension) Rules 1972 and that the details of my family already furnished by me in Form 3 are complete and up to date.
              </p>
              <p>
                3. I enclose herewith:-<br />
                <span className="pl-4 inline-block">i) Two specimen signature of mine duly attested.</span><br />
                <span className="pl-4 inline-block">ii) Three copies of a pass port size Joint photograph with Wife/ Husband.</span><br />
                <span className="pl-4 inline-block">iii) Two slips each bearing my left hand thumb and finger impression duly attested.</span><br />
                <span className="pl-4 inline-block">iv) Two slips showing the particulars of my height and identification marks duly attested.</span>
              </p>
              <p>
                4. My present address, and my address after retirement will be the same..
              </p>
            </div>
            <div className="grid grid-cols-2 text-xs font-serif mt-12 pt-6">
              <div>
                Place: Pune<br />
                Date: {formatDateDash(application_date)}
              </div>
              <div className="text-right pr-6">
                Signature: _______________________<br />
                Designation: {employee.designation}
              </div>
            </div>
            <div className="text-[10px] font-serif border-t border-black mt-8 pt-2 leading-tight">
              Only two Copies of pass port size photograph of self need be furnished if the Govt. servant is governed by Rule 54 of the CCS (P) Rule 1972 and is unmarried or a widow or a widower.<br />
              (ii) If the Govt. servant is governed by Rule 55 of CCS (P) rule 1972.
            </div>
          </div>
        );
      case 2:
        return (
          <div className="pension-form-page font-serif text-xs text-black px-6 py-4">

            {/* ================= HEADER ================= */}
            <div className="text-center border-b border-black pb-1 mb-1">
              <div className="font-bold text-lg tracking-wide">
                IDENTIFICATION FORM FOR PENSIONERS
              </div>

              <div className="text-[10px] mt-1">
                Specimen Signature / Left Hand Thumb impression:
                <span className="font-bold ml-1">
                  {employee.honorific} {employee.full_name}
                  {spouseName ? ` & SMT. ${spouseName.toUpperCase()}` : ''}
                </span>
              </div>
            </div>


            {/* ================= SPECIMEN SIGNATURE ================= */}

            <div className="mt-4">

              <div className="relative h-12 mb-4">
                <span className="absolute left-0">1.</span>
                <span className="absolute left-[220px]">1</span>
              </div>

              <div className="relative h-12 mb-4">
                <span className="absolute left-0">2.</span>
                <span className="absolute left-[220px]">2</span>
              </div>

            </div>


            {/* ================= OFFICER SIGNATURE ================= */}

            <div className="text-right font-bold text-[10px] mb-6">
              CHIEF EXECUTIVE OFFICER, PUNE.
            </div>


            {/* ================= JOINT PHOTOGRAPH ================= */}

            <div className="mb-1">
              Photo/joint Photograph of impression:

              <span className="font-bold ml-1">
                {employee.honorific} {employee.full_name}
                {spouseName ? ` & SMT. ${spouseName.toUpperCase()}` : ''}
              </span>
            </div>

            <div className="border-b border-black"></div>


            {/* Blank photograph area */}
            <div className="h-40"></div>


            {/* ================= SPOUSE PHOTOGRAPH ================= */}

            <div className="mb-1">
              Photograph of impression:

              <span className="font-bold ml-1">
                {spouseName
                  ? `SMT. ${spouseName.toUpperCase()}`
                  : '______________________'}
              </span>
            </div>

            <div className="border-b border-black"></div>


            {/* Blank photograph area */}
            <div className="h-40"></div>


            {/* ================= THUMB & FINGER IMPRESSIONS ================= */}

            <div className="mb-1">
              Left Hand Thumb & Finger Impressions:

              <span className="font-bold ml-1">
                {employee.honorific} {employee.full_name}
                {spouseName ? ` & SMT. ${spouseName.toUpperCase()}` : ''}
              </span>
            </div>


            {/* Two-part impression box */}
            <div className="grid grid-cols-2 border border-black h-28 mb-8">
              <div className="border-r border-black"></div>

              <div></div>
            </div>


            {/* ================= OFFICER SIGNATURE ================= */}

            <div className="text-right font-bold text-[10px] mb-8">
              CHIEF EXECUTIVE OFFICER, PUNE.
            </div>


            {/* ================= IDENTIFICATION MARKS ================= */}

            <div className="grid grid-cols-2 gap-8">

              {/* Pensioner */}
              <div>
                <div className="mb-1">
                  Particulars of Height & Identification Marks of impression
                </div>

                <div className="font-bold border-b border-black inline-block min-w-[250px] mb-1">
                  {employee.honorific} {employee.full_name}
                </div>

                <div className="leading-relaxed">
                  <div>
                    1. Height&nbsp;&nbsp;
                    <span className="inline-block border-b border-black w-20"></span>
                  </div>

                  <div>
                    2. Identification Mark 1:
                    <span className="inline-block border-b border-black w-24 ml-1"></span>
                  </div>

                  <div>
                    3. Identification Mark 2:
                    <span className="inline-block border-b border-black w-24 ml-1"></span>
                  </div>
                </div>
              </div>


              {/* Spouse */}
              <div className="pt-5">

                <div className="font-bold border-b border-black inline-block min-w-[250px] mb-1">
                  {spouseName
                    ? `SMT. ${spouseName.toUpperCase()}`
                    : '______________________'}
                </div>

                <div className="leading-relaxed">

                  <div>
                    1. Height&nbsp;&nbsp;
                    <span className="inline-block border-b border-black w-20"></span>
                  </div>

                  <div>
                    2. Identification Mark:
                    <span className="inline-block border-b border-black w-24 ml-1"></span>
                  </div>

                </div>
              </div>

            </div>


            {/* ================= FINAL SIGNATURE ================= */}

            <div className="flex justify-end mt-12">
              <div className="text-center font-bold text-[10px]">
                CHIEF EXECUTIVE OFFICER,<br />
                PUNE CANTONMENT BOARD
              </div>
            </div>

          </div>
        );
      case 3:
        return (
          <div className="pension-form-page font-serif text-xs" style={{ padding: '8mm 15mm' }}>
            <div className="text-center text-[10px] mb-1">Civil Pensions (Commutation) Rules</div>
            <div className="text-center font-bold text-sm mb-3">
              FORM OF APPLICATION FOR COMMUTATION OF PENSION<br />
              WITHOUT MEDICAL EXAMINATION
            </div>
            <div className="border border-black w-24 h-28 float-right flex items-center justify-center text-center text-[10px] text-gray-400 mb-1">
              [ Photo Place ]
            </div>
            <div className="mb-4 clear-left">
              <strong>To,</strong><br />
              The President<br />
              Cantonment Board, Pune - 1
            </div>
            <div className="text-center font-bold mb-4">
              SUBJECT: COMMUTATION OF PENSION WITHOUT MEDICAL EXAMINATION.
            </div>
            <p className="mb-3 text-justify leading-relaxed">
              Sir,<br />
              I furnish below the relevant particulars and request that I may be permitted to commute a part of my pension as indicated below (an attested copy of my photography is pasted in this application and an unattested copy is enclosed).
            </p>
            <table className="w-full border-collapse text-xs mb-3">
              <tbody>
                <tr className="align-top">
                  <td className="w-8 py-1">1.</td>
                  <td className="w-1/2 py-1">Name in Block letters</td>
                  <td className="py-1">: <strong>{employee.full_name.toUpperCase()}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">2.</td>
                  <td className="py-1">Date of Birth</td>
                  <td className="py-1">: <strong>{formatDateDash(employee.date_of_birth)}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">3.</td>
                  <td className="py-1">Date of superannuation on attaining the age of 58 years (or 60 years in the case of Group 'B' employees.)</td>
                  <td className="py-1">: <strong>{formatDateDash(employee.retirement_date)}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">4.</td>
                  <td className="py-1">Designation of the post held at the time of superannuation and the name of the Ministry Department/ Office</td>
                  <td className="py-1">: <strong>{employee.designation.toUpperCase()}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">5.</td>
                  <td className="py-1">Amount of Pension sanctioned and whether it is provisional or final</td>
                  <td className="py-1">: <strong>{pension.pension_amount ? `₹${pension.pension_amount} (Provisional)` : ''}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">6.</td>
                  <td className="py-1">Class of pension as defined in Chapter V of CCS (Pension) Rules 1972</td>
                  <td className="py-1">: <strong>{pension.pension_class ? pension.pension_class.toUpperCase() : ''}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">7.</td>
                  <td className="py-1">Name of Treasury of Bank & Account Number from which pension is being draw</td>
                  <td className="py-1">: <strong>{pensioner_profile.bank_name ? `${pensioner_profile.bank_name} / A/C: ${pensioner_profile.bank_account_number || ''}` : 'Cantonment Board Office, Pune'}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">8.</td>
                  <td className="py-1">Name of Treasury or Bank through which the commuted value is desired to be paid if payment us not desired through the Accounts Officer who authorized the Pension</td>
                  <td className="py-1">: <strong>{pensioner_profile.bank_name ? `${pensioner_profile.bank_name} / ${pensioner_profile.bank_branch || ''}` : 'Cantonment Board Office, Pune'}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">9.</td>
                  <td className="py-1">Designation of the Accounts Officer & the Number and date the PPO if issued</td>
                  <td className="py-1">: <strong>{pensioner_profile.ppo_number ? `PPO No: ${pensioner_profile.ppo_number}` : 'The President Cantt Board, Pune'}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">10.</td>
                  <td className="py-1">Amount (in whole rupees) or percentage of Pension proposed to be commuted</td>
                  <td className="py-1">: <strong>{commutation.pension_proposed_to_be_commuted ? `₹${commutation.pension_proposed_to_be_commuted} (40%)` : ''}</strong></td>
                </tr>
                <tr className="align-top">
                  <td className="w-8 py-1">11.</td>
                  <td className="py-1">Particulars of any application for commutation of pension made previously & whether Appeared before any medical authority or not</td>
                  <td className="py-1">: </td>
                </tr>
              </tbody>
            </table>
            <div className="grid grid-cols-2 mt-2">
              <div>
                Date: {formatDateDash(application_date)}<br />
                Place: Pune
              </div>
              <div className="text-right">
                Yours faithfully,<br /><br />
                <strong>{employee.full_name}</strong>
              </div>
            </div>
            <div className="border-t border-black mt-3 pt-2">
              <div className="text-center font-bold mb-1">PART - II<br />ACKNOWLEDGEMENT</div>
              <p className="mb-2 text-justify">
                Received from <strong>Shri./Smt. {employee.full_name}, {employee.designation}</strong>, (Former designation). In part I of Form I for the commutation of a fraction of pension without medical examination.
              </p>
              <div className="grid grid-cols-2 text-xs">
                <div>
                  Place: Pune<br />
                  Date: {formatDateDash(application_date)}
                </div>
                <div className="text-right">
                  Signature: _______________________<br />
                  Head of Office
                </div>
              </div>
              <div className="text-[10px] text-gray-500 mt-2 leading-tight">
                NOTE: This acknowledgement is to be signed stamped and dated and is to be detached from the Form and handed over to the applicant. If the form has been received by the post, it has to be acknowledged on the same day and Acknowledgements send under registered cover.
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="pension-form-page font-serif text-xs">
            <div className="text-center font-bold text-xs mb-6">FORM 'G'</div>
            <div className="text-center font-bold text-sm tracking-wider leading-relaxed mb-8">
              DECLARATION TO BE FURNISHED BY A PERSON TO WHOM AN ANTICIPATORY<br />
              DEATH- CUM- RETIREMENT GRATUITY IS SANCTION
            </div>
            <p className="mb-6 leading-relaxed text-justify">
              Whereas President Cantt. Board Pune has consented provisionally to advance a sum of<br /><br />
              Rs <strong>{gratuity.gratuity_amount ? `₹${gratuity.gratuity_amount}/-` : '___________/-'}</strong> (Rupees <strong>{gratuity.gratuity_in_words || '____________________________________________________________________'}</strong>)
            </p>
            <p className="mb-12 leading-relaxed text-justify">
              In anticipation of the completion of the enquiry, necessary to enable the Government to fix the amount of Death-Cum- Retirement Gratuity payable to me. I hereby acknowledge that in accepting this advance I fully understand that the Death-Cum-Retirement gratuity payable to me is subject to revision on the completion of necessary formal enquiries and I promise to base no objection to such revision on the ground that the provisional Death- Cum- retirement Gratuity which may be finally sanctioned to me. I further promise to repay an amount advanced to me in excess of the Death- Cum- Retirement Gratuity that may be finally sanctioned to me.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-12">
              <div>
                Date: {formatDateDash(application_date)}
              </div>
              <div className="text-right pr-6">
                Signature: _______________________<br /><br />
                Name : <strong>Shri./Smt. {employee.full_name}</strong><br /><br />
                Designation: <strong>{employee.designation}</strong>.
              </div>
            </div>
            <div className="space-y-4">
              <div>
                Witness No. 1: ____________________________________
              </div>
              <div>
                Address: _________________________________________
              </div>
              <div className="pt-2">
                Witness No. 2: ____________________________________
              </div>
              <div>
                Address: _________________________________________
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="pension-form-page font-serif text-xs">
            <div className="text-center font-bold mb-1">FORM-1</div>
            <div className="text-center font-bold mb-1">[See Rule 53 (1)]</div>
            <div className="text-center font-bold text-sm tracking-wider mb-6">
              NOMINATION FOR DEATH-CUM-RETIREMENT GRATUITY
            </div>
            <p className="mb-4 leading-relaxed text-justify">
              When the Government servant has family and wishes to nominate one member, thereof, I<br />
              <strong>Shri./Smt. {employee.full_name}, {employee.designation}</strong> hereby nominated the person/persons mentioned below who is / are member (s) of my family, and confer, on him / them the tight to receive to the extent specified below, any gratuity the payment of which may be authorized by the Central Government in the event of my death while in service and right to receive on my death to the extent specified below, any gratuity which having become admissible to me on retirement may remain unpaid at my death:
            </p>
            <table className="w-full border border-black border-collapse text-[10px] text-center mb-6">
              <thead>
                <tr className="bg-gray-100 divide-x divide-y divide-black border-b border-black">
                  <th className="p-1 border-r border-black" colSpan={4}>ORIGINAL NOMINEE(S)</th>
                  <th className="p-1" colSpan={2}>ALTERNATE NOMINEE(S)</th>
                </tr>
                <tr className="bg-gray-50 divide-x divide-black border-b border-black text-[9px]">
                  <th className="p-1 w-1/3">Names & address of nominee/ nominees</th>
                  <th className="p-1 w-16">Relationship with the Govt. servant</th>
                  <th className="p-1 w-12">Age</th>
                  <th className="p-1 w-16">Amount of share of gratuity payable to each *</th>
                  <th className="p-1 w-1/3">Name, Address, relationship and of the person of persons, if any to whom the right conferred on the nominee predeceasing the Govt. servant or the nominee dying after the death of the Govt. servant but before receiving payment of gratuity</th>
                  <th className="p-1 w-16">Amount of share of gratuity payable to each **</th>
                </tr>
                <tr className="divide-x divide-black border-b border-black text-[8px] bg-gray-100">
                  <th className="p-0.5">1</th>
                  <th className="p-0.5">2</th>
                  <th className="p-0.5">3</th>
                  <th className="p-0.5">4</th>
                  <th className="p-0.5">5</th>
                  <th className="p-0.5">6</th>
                </tr>
              </thead>
              <tbody>
                {nominees.length > 0 ? (
                  nominees.map((n, idx) => (
                    <tr key={idx} className="divide-x divide-y divide-black align-top text-[9px]">
                      <td className="p-1 text-left">{n.nominee_name}<br />{n.address_line1} {n.address_line2}</td>
                      <td className="p-1">{n.relationship}</td>
                      <td className="p-1">{n.date_of_birth ? `${new Date().getFullYear() - new Date(n.date_of_birth).getFullYear()} Yrs` : ''}</td>
                      <td className="p-1">{n.benefit_percentage}%</td>
                      <td className="p-1 text-left">{n.guardian_name ? `${n.guardian_name} (${n.guardian_relationship})` : '-'}</td>
                      <td className="p-1">-</td>
                    </tr>
                  ))
                ) : (
                  <tr className="h-12 divide-x divide-black">
                    <td className="p-1"></td>
                    <td className="p-1"></td>
                    <td className="p-1"></td>
                    <td className="p-1"></td>
                    <td className="p-1"></td>
                    <td className="p-1"></td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="text-[9px] space-y-1 mb-6 leading-tight">
              <p>* This column should be filled in so as to cover the whole amount of the gratuity.</p>
              <p>** The amount/share of the gratuity shown in this column should cover the whole amount/share payable to the original nominee(s).</p>
              <p>This nomination supersedes the nomination made by me earlier on _______________ which stands cancelled.</p>
              <p>NOTE: (i) The Govt. servant shall draw lines across the blank space below the last entry to prevent insertion of any name after he has signed. (ii) Strike out which is not applicable Dated this _______________ day of _______________ 20___</p>
            </div>
            <div className="grid grid-cols-2 text-xs pt-4">
              <div>
                1. &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;  &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;  &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; 1. <br />
                2. &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;  &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;  &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; 2.
              </div>
              <div className="text-center">
                &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; Signature of Government Servant<br />
                &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; (To be filled in by the Head of Office)
              </div>
            </div>
            <div className="grid grid-cols-2 text-xs mt-8">
              <div>
                Nominates by: <br />
                {/* Designation: <strong>{employee.designation}</strong><br /> */}
                Designation: <br />
                Office of the Cantt. Board Pune
              </div>
              <div className="text-left ml-1">
                &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; Signature of Head of Office: <br />
                &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; Date: <br />
                &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; Designation: <br /><br />
                <strong>&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; (CHIEF EXECUTIVE OFFICER,PUNE)</strong>
              </div>
            </div>
          </div >
        );
      case 6:
        return (
          <div className="pension-form-page font-serif text-xs px-6 py-5">

            {/* FORM HEADER */}
            <div className="text-center mb-4">
              <div className="font-bold">FORM 3</div>
              <div className="font-bold">(See Rule 54(12))</div>

              <div className="font-bold text-sm tracking-wider mt-3">
                DETAILS OF FAMILY
              </div>
            </div>


            {/* EMPLOYEE DETAILS */}
            <div className="mb-5 space-y-2">

              <div className="flex">
                <div className="w-[200px]">
                  Name of Government servant
                </div>
                <div className="mr-2">:</div>
                <div>
                  <strong>
                    {employee.honorific} {employee.full_name}
                  </strong>
                </div>
              </div>

              <div className="flex">
                <div className="w-[200px]">
                  Designation
                </div>
                <div className="mr-2">:</div>
                <div>
                  <strong>{employee.designation}</strong>
                </div>
              </div>

              <div className="flex">
                <div className="w-[200px]">
                  Date of Birth
                </div>
                <div className="mr-2">:</div>
                <div>
                  <strong>
                    {formatDateDash(employee.date_of_birth)}
                  </strong>
                </div>
              </div>

              <div className="flex">
                <div className="w-[200px]">
                  Date of Appointment
                </div>
                <div className="mr-2">:</div>
                <div>
                  <strong>
                    {formatDateDash(employee.join_date)}
                  </strong>
                </div>
              </div>

              <div className="flex mt-3">
                <div>
                  Details of the members of my FAMILY ** as on
                </div>

                <div className="ml-2">
                  <strong>
                    {formatDateDash(application_date)}
                  </strong>
                </div>
              </div>

            </div>


            {/* FAMILY DETAILS TABLE */}
            <table className="w-full border border-black border-collapse text-xs mb-5">

              <thead>

                {/* MAIN HEADER */}
                <tr className="border-b border-black">

                  <th className="border-r border-black p-1 w-[11%] text-center font-semibold">
                    Serial<br />No.
                  </th>

                  <th className="border-r border-black p-1 w-[25%] text-center font-semibold">
                    Name of the Members<br />
                    of family **
                  </th>

                  <th className="border-r border-black p-1 w-[15%] text-center font-semibold">
                    Date of<br />Birth
                  </th>

                  <th className="border-r border-black p-1 w-[18%] text-center font-semibold">
                    Relationship<br />
                    With the<br />
                    Officer
                  </th>

                  <th className="border-r border-black p-1 w-[15%] text-center font-semibold">
                    Initials of<br />
                    Head of<br />
                    Office
                  </th>

                  <th className="p-1 w-[16%] text-center font-semibold">
                    Remarks
                  </th>

                </tr>


                {/* COLUMN NUMBERS */}
                <tr className="border-b border-black text-center">

                  <th className="border-r border-black p-1 font-normal">
                    1.
                  </th>

                  <th className="border-r border-black p-1 font-normal">
                    2.
                  </th>

                  <th className="border-r border-black p-1 font-normal">
                    3.
                  </th>

                  <th className="border-r border-black p-1 font-normal">
                    4.
                  </th>

                  <th className="border-r border-black p-1 font-normal">
                    5.
                  </th>

                  <th className="p-1 font-normal">
                    6.
                  </th>

                </tr>

              </thead>


              <tbody>

                {nominees.length > 0 ? (

                  nominees.map((n, idx) => (

                    <tr
                      key={idx}
                      className="border-b border-black last:border-b-0 align-middle"
                    >

                      {/* SERIAL NUMBER */}
                      <td className="border-r border-black p-2 text-center">
                        {idx + 1}.
                      </td>


                      {/* FAMILY MEMBER NAME */}
                      <td className="border-r border-black p-2">
                        <strong>
                          {n.nominee_name}
                        </strong>
                      </td>


                      {/* DATE OF BIRTH */}
                      <td className="border-r border-black p-2 text-center">
                        {formatDateDash(n.date_of_birth)}
                      </td>


                      {/* RELATIONSHIP */}
                      <td className="border-r border-black p-2">
                        {n.relationship}
                      </td>


                      {/* HEAD OF OFFICE */}
                      <td className="border-r border-black p-2">
                      </td>


                      {/* REMARKS */}
                      <td className="p-2">
                        {n.guardian_name ? 'Minor' : ''}
                      </td>

                    </tr>

                  ))

                ) : (

                  <tr className="h-24">

                    <td className="border-r border-black"></td>
                    <td className="border-r border-black"></td>
                    <td className="border-r border-black"></td>
                    <td className="border-r border-black"></td>
                    <td className="border-r border-black"></td>
                    <td></td>

                  </tr>

                )}

              </tbody>

            </table>


            {/* UNDERTAKING */}
            <div className="text-left leading-relaxed mb-5 px-8">

              &nbsp; &nbsp; &nbsp; I hereby undertake to keep the above particular up to date by notifying
              to the Audit Officer/Head of Office any addition or alteration.

            </div>


            {/* PLACE / DATE + SIGNATURE */}
            <div className="grid grid-cols-2 mb-5">

              <div className="leading-relaxed">

                <div>
                  <strong>PLACE:</strong> Pune
                </div>

                <div>
                  <strong>DATE:</strong>{' '}
                  <strong>
                    {formatDateDash(application_date)}
                  </strong>
                </div>

              </div>


              <div className="text-right pt-7 pr-2">

                <strong>
                  SIGNATURE OF GOVERNMENT SERVANT
                </strong>

              </div>

            </div>


            {/* FAMILY DEFINITION */}
            <div className="leading-relaxed mb-3">

              <div className="font-bold mb-1">
                ** Family for this purpose means
              </div>

              <div className="pl-4">

                <div>
                  (a) Wife, in the case of a male Government servant.
                </div>

                <div>
                  (b) Husband, in the case of a female Government servant.
                </div>

                <div>
                  (c) Sons below eighteen years of age and unmarried daughter below
                  twenty one years of age, including such son and daughter adopted
                  legally before retirement.
                </div>

              </div>

            </div>


            {/* NOTE */}
            <div className="leading-relaxed">

              <strong>NOTE:-</strong>{' '}
              Wife and husband shall include respectively judicially separated wife
              and husband.

            </div>


            {/* OFFICER SIGNATURE */}
            <div className="text-right mt-16 pr-4 font-bold leading-relaxed">

              CHIEF EXECUTIVE OFFICER<br />
              PUNE CANTONMENT BOARD

            </div>

          </div>
        );
      case 7:
        return (
          <div className="pension-form-page font-serif text-xs px-7 py-6">

            {/* ================= CERTIFICATE ================= */}
            <div className="text-center font-bold text-sm tracking-wide mb-4">
              CERTIFICATE
            </div>


            {/* EMPLOYEE DETAILS */}
            <div className="mb-4 leading-relaxed">

              <div className="flex">
                <div className="w-36">Name</div>
                <div className="w-4">:</div>
                <div>
                  Shri./Smt. {employee.full_name}
                </div>
              </div>

              <div className="flex">
                <div className="w-36">Designation</div>
                <div className="w-4">:</div>
                <div>
                  {employee.designation}
                </div>
              </div>

              <div className="flex">
                <div className="w-36">Date of retired</div>
                <div className="w-4">:</div>
                <div>
                  {formatDateDash(employee.retirement_date)}
                </div>
              </div>

            </div>


            {/* CERTIFICATE CONTENT */}
            <div className="mb-4 leading-relaxed text-justify">

              <div className="pl-8">
                It is hereby certified that no temporary service was rendered in a
                non pensionable post, daily rated or casual establishment.
              </div>

            </div>


            {/* CERTIFICATE POINTS */}
            <div className="mb-7 leading-relaxed">

              <div className="grid grid-cols-[35px_1fr] mb-1">
                <div>2.</div>
                <div>
                  The post held by the individual was born in regular establishment.
                </div>
              </div>

              <div className="grid grid-cols-[35px_1fr] mb-1">
                <div>3.</div>
                <div>
                  The post has not been declared non pensionable under any general
                  of specific orders.
                </div>
              </div>

              <div className="grid grid-cols-[35px_1fr] mb-1">
                <div>4.</div>
                <div>
                  The post held by the individual was paid from general revenue as
                  distinct from contingencies.
                </div>
              </div>

              <div className="grid grid-cols-[35px_1fr]">
                <div>5.</div>
                <div>
                  The post has no other retirement benefits (e.g. the subscribes own
                  contribution etc.) admission under adhoc orders.
                </div>
              </div>

            </div>


            {/* HEAD OF OFFICE */}
            <div className="text-right mb-3 pr-8 leading-relaxed">

              <div>
                Head of Office
              </div>

              <div>
                Chief Executive Officer,
              </div>

            </div>


            {/* STATION / DATE */}
            <div className="mb-12 leading-relaxed">

              <div className="flex">
                <div className="w-14">
                  Station:
                </div>

                <div>
                  Pune
                </div>
              </div>


              <div className="flex border-b border-black w-[520px]">
                <div className="w-14">
                  Date
                </div>

                <div className="w-4">
                  :
                </div>

                <div>
                  {formatDateDash(application_date)}
                </div>
              </div>

            </div>


            {/* ================= OFFICE NUMBER / DATE ================= */}
            <div className="flex justify-end mb-10 pr-10">

              <div className="w-48 leading-relaxed">

                <div className="flex border-b border-black">
                  <div>No.</div>
                  <div className="flex-1"></div>
                </div>

                <div>
                  Cantonment Executive Officer
                </div>

                <div className="flex">
                  <span>Pune – 1</span>
                  <span className="ml-8">Date.</span>
                </div>

              </div>

            </div>


            {/* ================= PART II OFFICE ORDER ================= */}
            <div className="text-center font-bold text-sm tracking-wide mb-5">
              PART II OFFICE ORDER
            </div>


            {/* REFERENCE */}
            <div className="text-center mb-9 leading-relaxed">

              <div>
                REFERENCE: Ministry of Defence Southern Command, Pune
              </div>

              <div>
                Letter No. 2111/Pension/II/MLC, dated. 12.02.1978
              </div>

            </div>


            {/* PENSION ESTABLISHMENT TEXT */}
            <div className="mb-12 leading-relaxed text-justify">

              <div className="pl-5">

                Shri./Smt. {employee.full_name}, Designation {employee.designation},
                has been transferred to Pension establishment with effect from{' '}

                <strong>
                  {pension?.pension_commencement_date
                    ? formatDateDash(pension.pension_commencement_date)
                    : formatDateDash(employee.retirement_date)}
                </strong>

                .

              </div>

            </div>


            {/* FINAL SIGNATURE */}
            <div className="text-right font-bold pr-6 mt-14">
              CHIEF EXECUTIVE OFFICER, PUNE
            </div>

          </div>
        );
      case 8:
        return (
          <div className="pension-form-page font-serif text-xs">
            <div className="text-center font-bold mb-4">Regd.No.Cantt. 34-B (Rule 61(1))</div>
            <div className="text-center font-bold text-sm tracking-wider mb-6">
              PENSION STATEMENT
            </div>
            <div className="grid grid-cols-2 gap-y-3 mb-6">
              <div>Name of Servant: <strong>{employee.full_name}</strong></div>
              <div>Date of Birth: <strong>{formatDateDash(employee.date_of_birth)}</strong></div>
              <div>Date of Appointment: <strong>{formatDateDash(employee.join_date)}</strong></div>
              <div>Post: <strong>{employee.designation}</strong></div>
              <div>Date of Confirmation: <strong>{employee.confirmation_date ? formatDateDash(employee.confirmation_date) : formatDateDash(employee.join_date)}</strong></div>
              <div>Date of Retired: <strong>{formatDateDash(employee.retirement_date)}</strong></div>
              <div>Scale of Pay: <strong>{employee.pay_scale || 'N/A'}</strong></div>
              <div>Name of Board where Served: <strong>Pune Cantonment Board</strong></div>
              <div className="col-span-2">
                Service Period: From <strong>{formatDateDash(employee.join_date)}</strong> to <strong>{formatDateDash(employee.retirement_date)}</strong> ({qualifying_service.net_qualifying_service?.toFixed(2) || 0} Years)
              </div>
            </div>
            <table className="w-full border border-black border-collapse text-center text-xs mb-8">
              <thead>
                <tr className="bg-gray-100 divide-x divide-black border-b border-black h-10">
                  <th className="p-1.5">Name of Board</th>
                  <th className="p-1.5">Period of Service</th>
                  <th className="p-1.5">Period of Qualifying Service</th>
                  <th className="p-1.5">Average Emolument</th>
                  <th className="p-1.5">Pro-rata Contribution (p.m.)</th>
                  <th className="p-1.5">Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr className="divide-x divide-black h-16 align-middle">
                  <td className="p-1.5 font-semibold">Pune Cantonment Board</td>
                  <td className="p-1.5">{qualifying_service.total_service_formatted || 'N/A'}</td>
                  <td className="p-1.5">{pension.qualifying_service_formatted || 'N/A'}</td>
                  <td className="p-1.5">₹{pension.average_emolument || employee.basic_pay || 0}</td>
                  <td className="p-1.5">-</td>
                  <td className="p-1.5"></td>
                </tr>
              </tbody>
            </table>
            <div className="grid grid-cols-2 text-xs mt-24 pt-12">
              <div>
                Pune<br />
                Date: {formatDateDash(application_date)}
              </div>
              <div className="text-right">
                Chief Executive Officer<br />
                Pune Cantonment Board
              </div>
            </div>
          </div>
        );
      case 9:
        return (
          <div className="pension-form-page font-serif text-xs px-6 py-5">

            {/* ===================== FORM HEADER ===================== */}

            <div className="text-right font-bold text-xs mb-1">
              FORM-5
            </div>

            <div className="text-center font-bold text-xs mb-1">
              (See Rule 57)
            </div>

            <div className="text-center font-bold text-sm tracking-wide mb-6">
              FAMILY PENSION NOMINATION
            </div>


            {/* ===================== NOMINATION STATEMENT ===================== */}

            <div className="mb-6 leading-relaxed text-justify">
              I,{" "}
              <strong>
                {employee.honorific} {employee.full_name}
              </strong>
              , Designation{" "}
              <strong>
                {employee.designation}
              </strong>
              , hereby nominate the person named below under Rule 57 of the
              Central Civil Services (Pension) Rules, 1972:
            </div>


            {/* ===================== NOMINATION TABLE ===================== */}

            <div className="w-full">

              <table className="w-full table-fixed border-collapse border border-black text-[9px]">

                {/* COLUMN WIDTH CONTROL */}
                <colgroup>

                  {/* 1 - Name */}
                  <col className="w-[6%]" />

                  {/* 2 - Relationship */}
                  <col className="w-[7%]" />

                  {/* 3 - Minor */}
                  <col className="w-[6%]" />

                  {/* 4 - Other nominee */}
                  <col className="w-[18%]" />

                  {/* 5 - Relationship with pensioner */}
                  <col className="w-[8%]" />

                  {/* 6 - DOB */}
                  <col className="w-[8%]" />

                  {/* 7 - Guardian */}
                  <col className="w-[20%]" />

                  {/* 8 - Contingency */}
                  <col className="w-[14%]" />

                  {/* 9 - Remarks */}
                  <col className="w-[13%]" />

                </colgroup>


                {/* ===================== TABLE HEADER ===================== */}

                <thead>

                  <tr className="border-b border-black align-middle">

                    {/* COLUMN 1 */}
                    <th className="border-r border-black p-1 text-center font-bold leading-tight whitespace-normal break-words">
                      Name of the nominee
                    </th>


                    {/* COLUMN 2 */}
                    <th className="border-r border-black p-1 text-center font-bold leading-tight whitespace-normal break-words">
                      Relationship with the nominee
                    </th>


                    {/* COLUMN 3 */}
                    <th className="border-r border-black p-1 text-center font-bold leading-tight whitespace-normal break-words">
                      If nominee is minor
                    </th>


                    {/* COLUMN 4 */}
                    <th className="border-r border-black p-1 text-center font-bold leading-tight whitespace-normal break-words">
                      Name and address of other nominee(s) under the Pensioner
                    </th>


                    {/* COLUMN 5 */}
                    <th className="border-r border-black p-1 text-center font-bold leading-tight whitespace-normal break-words">
                      Relationship with Pensioner
                    </th>


                    {/* COLUMN 6 */}
                    <th className="border-r border-black p-1 text-center font-bold leading-tight whitespace-normal break-words">
                      Date of Birth, if nominee is minor
                    </th>


                    {/* COLUMN 7 */}
                    <th className="border-r border-black p-1 text-center font-bold leading-tight whitespace-normal break-words">
                      Name and address of the guardian if the nominee is a minor
                      and guardian is appointed
                    </th>


                    {/* COLUMN 8 */}
                    <th className="border-r border-black p-1 text-center font-bold leading-tight whitespace-normal break-words">
                      Contingency on happening of which nomination will become
                      invalid
                    </th>


                    {/* COLUMN 9 */}
                    <th className="p-1 text-center font-bold leading-tight whitespace-normal break-words">
                      Remarks
                    </th>

                  </tr>


                  {/* ===================== COLUMN NUMBERS ===================== */}

                  <tr className="border-b border-black text-center font-bold">

                    <th className="border-r border-black p-1">
                      1
                    </th>

                    <th className="border-r border-black p-1">
                      2
                    </th>

                    <th className="border-r border-black p-1">
                      3
                    </th>

                    <th className="border-r border-black p-1">
                      4
                    </th>

                    <th className="border-r border-black p-1">
                      5
                    </th>

                    <th className="border-r border-black p-1">
                      6
                    </th>

                    <th className="border-r border-black p-1">
                      7
                    </th>

                    <th className="border-r border-black p-1">
                      8
                    </th>

                    <th className="p-1">
                      9
                    </th>

                  </tr>

                </thead>


                {/* ===================== TABLE BODY ===================== */}

                <tbody>

                  {nominees && nominees.length > 0 ? (

                    nominees.map((n, idx) => {

                      /*
                       * Calculate age correctly.
                       * This also considers whether the birthday has occurred
                       * in the current year.
                       */
                      let isMinor = false;

                      if (n.date_of_birth) {

                        const dob = new Date(n.date_of_birth);
                        const today = new Date();

                        let age =
                          today.getFullYear() -
                          dob.getFullYear();

                        const monthDifference =
                          today.getMonth() -
                          dob.getMonth();

                        const dayDifference =
                          today.getDate() -
                          dob.getDate();

                        if (
                          monthDifference < 0 ||
                          (
                            monthDifference === 0 &&
                            dayDifference < 0
                          )
                        ) {
                          age--;
                        }

                        isMinor = age < 18;
                      }


                      return (

                        <tr
                          key={idx}
                          className="border-b border-black align-top"
                        >

                          {/* ================= COLUMN 1 ================= */}

                          <td className="border-r border-black p-1.5 leading-tight break-words">
                            {n.nominee_name || ""}
                          </td>


                          {/* ================= COLUMN 2 ================= */}

                          <td className="border-r border-black p-1.5 text-center leading-tight break-words">
                            {n.relationship || ""}
                          </td>


                          {/* ================= COLUMN 3 ================= */}

                          <td className="border-r border-black p-1.5 text-center">
                            {isMinor ? "Yes" : "No"}
                          </td>


                          {/* ================= COLUMN 4 ================= */}

                          <td className="border-r border-black p-1.5 leading-tight break-words">
                            {n.other_nominee_name || ""}

                            {n.other_nominee_address && (
                              <div className="mt-1">
                                {n.other_nominee_address}
                              </div>
                            )}
                          </td>


                          {/* ================= COLUMN 5 ================= */}

                          <td className="border-r border-black p-1.5 text-center leading-tight break-words">
                            {n.other_nominee_relationship || ""}
                          </td>


                          {/* ================= COLUMN 6 ================= */}

                          <td className="border-r border-black p-1.5 text-center leading-tight">
                            {isMinor && n.date_of_birth
                              ? formatDateDash(n.date_of_birth)
                              : ""}
                          </td>


                          {/* ================= COLUMN 7 ================= */}

                          <td className="border-r border-black p-1.5 leading-tight break-words">

                            {isMinor && (
                              <>
                                {n.guardian_name && (
                                  <div>
                                    <strong>
                                      {n.guardian_name}
                                    </strong>
                                  </div>
                                )}

                                {n.guardian_relationship && (
                                  <div className="mt-1">
                                    Relationship:{" "}
                                    {n.guardian_relationship}
                                  </div>
                                )}

                                {n.guardian_address && (
                                  <div className="mt-1">
                                    {n.guardian_address}
                                  </div>
                                )}
                              </>
                            )}

                          </td>


                          {/* ================= COLUMN 8 ================= */}

                          <td className="border-r border-black p-1.5 leading-tight break-words">
                            {n.contingency || ""}
                          </td>


                          {/* ================= COLUMN 9 ================= */}

                          <td className="p-1.5 leading-tight break-words">
                            {n.remarks || ""}
                          </td>

                        </tr>
                      );
                    })

                  ) : (

                    /* EMPTY ROW */

                    <tr className="h-16">

                      <td className="border-r border-black"></td>

                      <td className="border-r border-black"></td>

                      <td className="border-r border-black"></td>

                      <td className="border-r border-black"></td>

                      <td className="border-r border-black"></td>

                      <td className="border-r border-black"></td>

                      <td className="border-r border-black"></td>

                      <td className="border-r border-black"></td>

                      <td></td>

                    </tr>

                  )}

                </tbody>

              </table>

            </div>


            {/* ===================== SIGNATURE AREA ===================== */}

            <div className="grid grid-cols-2 mt-8 text-xs">

              {/* LEFT SIDE */}

              <div className="leading-6">

                <div>
                  Place: <strong>Pune</strong>
                </div>

                <div>
                  Date:{" "}
                  <strong>
                    {formatDateDash(application_date)}
                  </strong>
                </div>


                <div className="mt-8">
                  Witness Signature:
                </div>

                <div className="border-b border-black w-48 h-5"></div>


                <div className="mt-3">
                  Name and Address:
                </div>

                <div className="border-b border-black w-48 h-5"></div>

              </div>


              {/* RIGHT SIDE */}

              <div className="text-right flex flex-col items-end">

                <div className="mt-10">
                  Signature of Pensioner
                </div>

                <div className="text-[10px] mt-1">
                  (or Thumb Impression)
                </div>

                <div className="border-b border-black w-48 h-8 mt-1"></div>

              </div>

            </div>


            {/* ===================== ACKNOWLEDGEMENT ===================== */}

            <div className="mt-10 text-[10px] leading-relaxed text-justify">

              Acknowledgement to be sent by the Head of Office certifying that
              the nomination has been received from{" "}

              <strong>
                {employee.honorific} {employee.full_name}
              </strong>

              {" "}Designation{" "}

              <strong>
                {employee.designation}
              </strong>

              {" "}and has been recorded.

            </div>


            {/* ===================== HEAD OF OFFICE ===================== */}

            <div className="grid grid-cols-2 mt-12 text-xs">

              <div>
                Place: Pune
                <br />

                Date: {formatDateDash(application_date)}
              </div>


              <div className="text-right">

                <div className="font-bold">
                  CHIEF EXECUTIVE OFFICER
                </div>

                <div>
                  PUNE CANTONMENT BOARD
                </div>

              </div>

            </div>

          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-100 rounded-xl shadow-2xl flex flex-col w-full max-w-5xl max-h-[90vh]">
        {/* Modal Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h3 className="text-lg font-bold text-slate-800">Pension Application Form</h3>
            <p className="text-xs text-slate-500">Preview and download the official 9-page application document</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={generatingPdf}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {generatingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Generating PDF...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" /> Download PDF
                </>
              )}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Content - Preview Container */}
        <div className="flex-1 overflow-auto p-8 flex items-start justify-center">
          <div className="bg-white shadow-lg border border-slate-300 rounded-sm scale-90 md:scale-100 origin-center transition-transform">
            {renderPage(currentPage)}
          </div>
        </div>

        {/* Modal Footer - Pagination controls */}
        <div className="bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between rounded-b-xl">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <span className="text-sm font-semibold text-slate-700">
            Page {currentPage} of 9
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(9, prev + 1))}
            disabled={currentPage === 9}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Offscreen container for PDF rendering containing all 9 pages sequentially */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '210mm' }}>
        <div ref={printContainerRef} className="pdf-print-root">
          <style>{`
            .pdf-print-root {
              width: 100%;
              background: #ffffff;
            }
            .pension-form-page {
              width: 210mm;
              height: 296mm;
              max-height: 296mm;
              max-width: 210mm;
              margin: 0 !important;
              padding: 20mm 20mm;
              box-sizing: border-box;
              background: white;
              page-break-after: always;
              break-after: page;
              font-family: 'Times New Roman', Times, serif;
              font-size: 11pt;
              line-height: 1.4;
              color: #000000;
              position: relative;
              overflow: hidden;
            }
            .pension-form-page:last-child {
              page-break-after: avoid;
              break-after: avoid;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              padding: 4px;
            }
          `}</style>
          {renderPage(1)}
          {renderPage(2)}
          {renderPage(3)}
          {renderPage(4)}
          {renderPage(5)}
          {renderPage(6)}
          {renderPage(7)}
          {renderPage(8)}
          {renderPage(9)}
        </div>
      </div>
    </div>
  );
};

export default Employee360PensionApplicationForm;
